import type { CourseBlock, CourseDetails } from "@/utils/courseJourneys";

export interface CourseEditorDraft {
  revision: string;
  details: CourseDetails;
  blocks: CourseBlock[];
  selected: number;
  expanded: string | null;
}
export const courseEditorDraftKey = (
  userId: string,
  journeyId: string,
  admin: boolean,
) => `jp:course-editor:${userId}:${admin ? "admin" : "creator"}:${journeyId}`;

export function readCourseEditorDraft(
  key: string | undefined,
  revision: string,
): CourseEditorDraft | null {
  if (!key) return null;
  try {
    const value = JSON.parse(
      sessionStorage.getItem(key) || "null",
    ) as CourseEditorDraft | null;
    if (
      !value ||
      value.revision !== revision ||
      !Array.isArray(value.blocks) ||
      !value.details ||
      !Number.isInteger(value.selected)
    )
      return null;
    return value;
  } catch {
    return null;
  }
}
export function writeCourseEditorDraft(key: string, value: CourseEditorDraft) {
  sessionStorage.setItem(key, JSON.stringify(value));
}
export function clearCourseEditorDraft(key?: string) {
  try {
    if (key) sessionStorage.removeItem(key);
  } catch {
    /* A saved draft is authoritative. */
  }
}

/** Author return targets are fixed in-app routes, never arbitrary redirects. */
export function getJourneyAuthorReturn(params: URLSearchParams): string | null {
  const raw = params.get("journeyReturn");
  if (!raw) return null;
  const [path, query = ""] = raw.split("?");
  if (
    !/^\/(?:create\/journey|admin\/journey-builder)\/(?:new|[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12})$/i.test(
      path,
    )
  )
    return null;
  const source = new URLSearchParams(query);
  const clean = new URLSearchParams();
  if (/^[1-9]\d{0,2}$/.test(source.get("previewSession") || ""))
    clean.set("previewSession", source.get("previewSession")!);
  if (/^[0-9a-f-]{36}$/i.test(source.get("previewUnit") || ""))
    clean.set("previewUnit", source.get("previewUnit")!);
  return `${path}${clean.size ? `?${clean}` : ""}`;
}
export function carryJourneyAuthorReturn(
  path: string,
  params: URLSearchParams,
): string {
  const destination = getJourneyAuthorReturn(params);
  return destination
    ? `${path}?${new URLSearchParams({ journeyReturn: destination })}`
    : path;
}

function stableJson(value: unknown): string {
  return JSON.stringify(value, (_key, item: unknown) =>
    item && typeof item === "object" && !Array.isArray(item)
      ? Object.fromEntries(
          Object.entries(item as Record<string, unknown>)
            .filter(([, entry]) => entry !== undefined)
            .sort(([a], [b]) => a.localeCompare(b)),
        )
      : item,
  );
}
/** Block ids are random on a new draft and the joined program is display data. */
function comparableBlocks(blocks: CourseBlock[], ignoreIds: boolean) {
  return blocks.map(({ program: _program, id, ...block }) =>
    ignoreIds ? block : { id, ...block },
  );
}
/** True when a restored draft holds edits the author hasn't saved. A restored
 * copy that matches the starting point (or an empty new draft) is untouched. */
export function courseDraftHasChanges(
  draft: { details: CourseDetails; blocks: CourseBlock[] },
  start: { details: CourseDetails; blocks: CourseBlock[] },
  isNew: boolean,
): boolean {
  if (stableJson(draft.details) !== stableJson(start.details)) return true;
  if (isNew && draft.blocks.length === 0) return false;
  return (
    stableJson(comparableBlocks(draft.blocks, isNew)) !==
    stableJson(comparableBlocks(start.blocks, isNew))
  );
}
